import type { VanillaLinkProps } from '@_/ui';
import type { Post } from '../../types/index.ts';
import type { TextPostProps } from '../TextPost/index.ts';
import type { ArticleFeedItemProps } from './ArticleFeedItem.tsx';
import clsx from 'clsx';
import { PostType } from '@_/constants';
import { MAX_TEXT_POST_WIDTH } from '@_/theme/constants.ts';
import { TextPost } from '../TextPost/index.ts';
import { ArticleFeedItem } from './ArticleFeedItem.tsx';

export interface PostFeedProps<TValue> extends Pick<TextPostProps, 'bleed'> {
  getActions?: (post: TValue) => React.ReactNode;
  getAuthor: (post: TValue) => {
    avatarUrl?: string | null;
    name: string;
    renderLink?: (props: VanillaLinkProps) => React.ReactNode;
    type: 'organization' | 'person';
  };
  getButtonVisibility?: (post: TValue) => { comment: boolean; like: boolean };
  getClickCommentHandler?: (post: TValue) => (event: React.MouseEvent) => void;
  getClickCommentsHandler?: (post: TValue) => (event: React.MouseEvent) => void;
  onToggleLike?: (post: TValue) => void;
  items: TValue[];
  renderCommentLink?: (post: TValue, props: VanillaLinkProps) => React.ReactNode;
  renderCommentsLink?: (post: TValue, props: VanillaLinkProps) => React.ReactNode;
  renderTitleLink?: (post: TValue, props: VanillaLinkProps) => React.ReactNode;
  spacing?: 'tight' | 'normal';
}

export function PostFeed<TValue extends Post>({
  items,
  getActions,
  getAuthor,
  getButtonVisibility,
  getClickCommentHandler,
  getClickCommentsHandler,
  onToggleLike,
  renderCommentLink,
  renderCommentsLink,
  renderTitleLink,
  spacing = 'normal',
  ...otherProps
}: PostFeedProps<TValue>) {
  return (
    <div
      className={clsx(
        'grid',
        spacing === 'tight' && 'gap-y-4',
        spacing === 'normal' && 'gap-y-4 sm:gap-y-6',
        MAX_TEXT_POST_WIDTH
      )}
    >
      {items.map(post => {
        const {
          avatarUrl,
          name: authorName,
          renderLink: renderAuthorLink,
          type: authorType
        } = getAuthor(post);

        const { comment: showCommentButton, like: showLikeButton } =
          getButtonVisibility?.(post) || {};

        const postProps: ArticleFeedItemProps | TextPostProps = {
          ...otherProps,
          actions: getActions?.(post),
          authorAvatarUrl: avatarUrl,
          authorName,
          authorType,
          onClickComment: getClickCommentHandler?.(post),
          onClickComments: getClickCommentsHandler?.(post),
          onToggleLike: () => onToggleLike?.(post),
          post,
          showCommentButton,
          showLikeButton,
          renderAuthorLink,
          renderCommentLink: renderCommentLink
            ? props => renderCommentLink(post, props)
            : undefined,
          renderCommentsLink: renderCommentsLink
            ? props => renderCommentsLink(post, props)
            : undefined
        };

        switch (post.type) {
          case PostType.Article:
            return (
              <ArticleFeedItem
                key={post.id}
                {...postProps}
                renderTitleLink={
                  renderTitleLink ? props => renderTitleLink(post, props) : undefined
                }
              />
            );
          case PostType.Text:
            return <TextPost key={post.id} {...postProps} />;
          default:
            return undefined;
        }
      })}
    </div>
  );
}
